// frontend/src/components/loading/SkeletonStatus.js
//
// The accessible, motion-aware shell every page skeleton renders into
// (SettingsPageSkeleton, AdminPageSkeleton - and the pages' own initial-load
// branches, which render the same body parts into it: see
// SettingsPanelSkeletons.js).
//
// WHY ONE SHELL. A page skeleton is a Suspense fallback that stands where a
// whole page will be. Four things must be true of every one of them, and
// written once they cannot drift apart:
//   1. It is ONE `role="status"` region (implicitly polite) holding the same
//      visually-hidden "טוען" / "Loading" the branded boot loader uses
//      (BRAND_BOOT_LABEL, read from BrandBootLoader.js rather than retyped,
//      so the owner-approved wording lives in one place); the grey shapes are
//      one aria-hidden subtree. No `aria-busy`: assistive tech holds back a
//      busy region's content, and these regions are inserted with their
//      label already inside - so the label is simply the region's text. A
//      screen reader reads it when the user reaches it; whether a region
//      that APPEARS with its text is also announced depends on the browser
//      and screen reader - none of them announce the shapes.
//   2. The shapes "breathe" (MUI Skeleton's pulse) and stop under BOTH motion
//      switches: the OS `prefers-reduced-motion: reduce`, and the site's own
//      "stop animations" toggle, which motion/motionSignal.js publishes as
//      `html.lf-motion-off` (the pattern BrandBootLoader.js and BrandSwitch.js
//      follow). Stopped, a shape rests at full opacity: a static skeleton.
//   3. The breath does not restart when one skeleton hands over to the next.
//      A load swaps parts several times (the route's chunk fallback, then a
//      tab's, then the panel's own data-load branch); every swap mounts new
//      shapes, and MUI's pulse would start again from full opacity each time
//      - a visible blink. So every shell phase-locks its pulse to one shared
//      clock (pulseDelayVar, stamped at commit by stampPulsePhase): a part
//      mounted mid-breath joins at the phase the outgoing one had.
//   4. Every shape has the same fill - see SKELETON_FILL.
//
// Direction: the shell sets none. It renders inside AuthLayout, whose root
// carries dir="rtl"/"ltr" for the site language (this app has no RTL theme),
// so flex rows and block widths mirror on their own, exactly as they do on the
// real pages the skeletons stand in for.
//
// Kept light (MUI Box + the shared visually-hidden recipe only), and never
// lazy itself: it renders inside the skeleton chunks and the page chunks alike,
// as the fallback FOR a lazy chunk, so it must never suspend.

import React from "react";
import { Box } from "@mui/material";
import useSiteIsHebrew from "../../hooks/useSiteIsHebrew";
import { VISUALLY_HIDDEN_SX } from "../../styles/visuallyHidden";
import { BRAND_BOOT_LABEL } from "../BrandBootLoader";

/**
 * The one fill of every skeleton shape: the midpoint of LeadCardSkeleton's
 * shimmer stops (#f0f0f0 -> #e0e0e0), the established skeleton grey in this
 * app - solid, because the page skeletons pulse rather than shimmer.
 */
export const SKELETON_FILL = "#e8e8e8";

/** The visually-hidden status label - the shell's only text. */
export const PAGE_SKELETON_LABEL = BRAND_BOOT_LABEL;

/** MUI Skeleton's pulse period (`2s ease-in-out 0.5s infinite`), in ms. */
export const PULSE_PERIOD_MS = 2000;

/** The CSS variable each shell sets to its phase-locked pulse delay. */
export const PULSE_DELAY_VAR = "--lf-skel-delay";

/**
 * The pulse delay that puts a skeleton mounted NOW in phase with every other
 * one: minus the time elapsed into the current period of a clock all of them
 * share (`performance.now()`, which starts with the page). A shape mounted at
 * t with delay -(t mod 2000) is at that very point of its breath, exactly
 * where one mounted earlier is - so a swap of parts does not blink.
 *
 * Returns null where there is no usable clock (no `performance`, a throwing
 * or non-finite `now()`): the shell then sets no variable and MUI's own 0.5s
 * delay applies - a restart, never an error.
 *
 * @returns {string|null} a CSS time in (-2000ms, 0ms], e.g. "-1234ms"
 */
export function pulseDelayVar() {
  try {
    const now = performance.now();
    if (!Number.isFinite(now) || now < 0) return null;
    // `|| 0` turns -0 into 0, so the string never reads "-0ms".
    return `${-Math.round(now % PULSE_PERIOD_MS) || 0}ms`;
  } catch {
    return null;
  }
}

/**
 * Ref callback that stamps the phase-locked delay on a shell's DOM node AT
 * COMMIT, once per mounted node.
 *
 * WHY COMMIT, NOT RENDER. The delay only phase-locks if it is computed at the
 * moment the animation starts, and the animation starts at the first style
 * recalc after React COMMITS the node. Render time is no stand-in: React can
 * hold a finished Suspense retry (up to ~300ms) before committing it, so a
 * delay computed while rendering lagged its animation's start by a different
 * amount per shell (measured 54ms vs 287ms on /settings) - a phase jump, the
 * very blink the shared clock exists to prevent. A ref callback runs in the
 * commit's layout phase, before the browser recalculates styles, so the lag
 * is only the wait for the next frame - a few ms, alike for every shell
 * (measured: chunk vs data skeleton within 15ms of each other).
 *
 * Module-level, so its identity never changes: React calls it on attach and
 * detach only, never on a re-render - a re-render cannot move the phase. A
 * node that already carries the variable keeps it (StrictMode's re-attach of
 * the same node). Written straight to `node.style`, not through React's
 * `style` prop (which would mean a re-render) or `sx` (the value differs per
 * mount, so it would mint a new emotion class every time); React never
 * touches a style it was not given. No clock -> no variable, and MUI's own
 * 0.5s delay applies; never throws.
 *
 * @param {HTMLElement|null} node
 */
export function stampPulsePhase(node) {
  if (!node) return;
  try {
    if (node.style.getPropertyValue(PULSE_DELAY_VAR)) return;
    const delay = pulseDelayVar();
    if (delay !== null) node.style.setProperty(PULSE_DELAY_VAR, delay);
  } catch {
    // No usable style object: MUI's own delay - a restart, never an error.
  }
}

// The fill, the delay and the motion gates hang off the SHELL, as descendant
// rules (`.css-shell .MuiSkeleton-root`, specificity 0,2,0), so they beat MUI's
// own single-class Skeleton rule without an `sx` on every shape. The gates'
// `animation: none` resets the delay along with the rest of the shorthand, so
// a stopped skeleton stays stopped whatever the variable holds.
const SHAPE = "& .MuiSkeleton-root";
const SHAPE_GATES_SX = {
  [SHAPE]: {
    backgroundColor: SKELETON_FILL,
    animationDelay: `var(${PULSE_DELAY_VAR}, 0.5s)`,
  },
  "@media (prefers-reduced-motion: reduce)": { [SHAPE]: { animation: "none" } },
  [`html.lf-motion-off ${SHAPE}`]: { animation: "none" },
};
const ROOT_SX = {
  // The label's containing block - see LABEL_SX.
  position: "relative",
  width: "100%",
  ...SHAPE_GATES_SX,
};

// The shared visually-hidden recipe, PINNED inside the shell's top-left
// corner. Left at its static position, the 1px box sat on the shell's
// inline-start edge - the RIGHT edge under dir="rtl" - and its -1px margin
// pushed it one pixel past the viewport: a 1px horizontal scroll, measured on
// the dev server at 360, 390 and 1366px. (BrandBootLoader's root clips its
// overflow, so it never showed there.) Absolutely positioned, the margin does
// no layout work, so zeroing it changes nothing else.
const LABEL_SX = { ...VISUALLY_HIDDEN_SX, top: 0, left: 0, margin: 0 };

/**
 * Status region wrapping a page skeleton's shapes.
 *
 * Accessibility: one `role="status"` region (implicitly `aria-live="polite"`)
 * holding a visually-hidden "טוען" / "Loading"; the shapes (`children`) are
 * one aria-hidden subtree. Renders no other text and never throws.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children the skeleton's shapes (decorative)
 * @param {object} [props.sx] the page's own outer-box sx (min-height, padding),
 *        merged after the shell's
 * @param {string} [props.testId] `data-testid` of the region
 * @param {boolean} [props.isHebrew] the language of the page it stands in for.
 *        Given, it wins over the stored site language, so the label follows
 *        the page through a language toggle; omitted, the site language
 *        (useSiteIsHebrew).
 */
function SkeletonStatus({ children, sx, testId, isHebrew: isHebrewProp }) {
  const [siteIsHebrew] = useSiteIsHebrew();
  const isHebrew = typeof isHebrewProp === "boolean" ? isHebrewProp : siteIsHebrew;
  return (
    <Box
      role="status"
      data-testid={testId}
      ref={stampPulsePhase}
      sx={[ROOT_SX, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Box aria-hidden="true">{children}</Box>
      <Box component="span" sx={LABEL_SX} lang={isHebrew ? "he" : "en"}>
        {isHebrew ? PAGE_SKELETON_LABEL.he : PAGE_SKELETON_LABEL.en}
      </Box>
    </Box>
  );
}

/**
 * The same fill, pulse clock and motion gates WITHOUT a status region: for a
 * secondary section that is still loading on a page whose one loading status
 * is already on screen elsewhere (Settings' Advanced tab: the groups panel
 * carries the status, the additional-emails card below it only breathes). A
 * second `role="status"` there would give assistive tech two "loading"
 * regions for one page. The whole subtree is aria-hidden; renders no text and
 * never throws.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children the shapes (decorative)
 * @param {object} [props.sx] extra sx, merged after the gates
 * @param {string} [props.testId] `data-testid` of the wrapper
 */
export function SkeletonShapes({ children, sx, testId }) {
  return (
    <Box
      aria-hidden="true"
      data-testid={testId}
      ref={stampPulsePhase}
      sx={[SHAPE_GATES_SX, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Box>
  );
}

export default SkeletonStatus;
